import { isValidElement } from "react";

import { ArrowDown, ArrowUp } from "lucide-react";

import { ReactComponent as LoadingIcon } from "@assets/svg/loading-spinner.svg";

import { Checkbox } from "@components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/ui/table";

import { cn } from "@utils/ui";

import { Column, DataTableProps } from "./types/data-table";

export const DataTable = <T,>({
  data,
  columns,
  isLoading = false,
  selectable = false,
  selectedItems = [],
  onSelectionChange,
  getItemId,
  onRowClick,
  className,
  cellClassName,
  emptyMessage = "No data available",
  loadingComponent,
  sortState,
  onSort,
  actionsHeaderLabel = "Actions",
  actions = [],
  showActions = false,
}: DataTableProps<T>) => {
  const handleSelectAll = (checked: boolean) => {
    if (!onSelectionChange) return;

    if (checked) {
      const allIds = data.map(getItemId);
      onSelectionChange(allIds);
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectItem = (itemId: string, checked: boolean) => {
    if (!onSelectionChange) return;

    if (checked) {
      onSelectionChange([...selectedItems, itemId]);
    } else {
      onSelectionChange(selectedItems.filter((id) => id !== itemId));
    }
  };

  const hasData = data.length > 0;
  const isAllSelected = hasData && selectedItems.length === data.length;
  const isSomeSelected = hasData && selectedItems.length > 0 && !isAllSelected;

  /**
   * Render a cell value. `Column.accessor` is typed to return a ReactNode, but
   * cells used to be wrapped in a blanket `String()` — so any element accessor
   * rendered as "[object Object]". Elements now pass through; primitives are
   * still coerced so numbers/booleans/dates print as before, and null/undefined
   * render as an empty cell rather than the literal "null".
   */
  const renderCell = (value: unknown): React.ReactNode => {
    if (value === null || value === undefined) return "";
    if (isValidElement(value)) return value;
    return String(value);
  };

  const getCellValue = (item: T, column: Column<T>) => {
    if (typeof column.accessor === "function") {
      return column.accessor(item);
    }

    const keys = column.accessor.split(".");
    let value: unknown = item;

    for (const key of keys) {
      value = (value as Record<string, unknown>)?.[key];
    }

    return value;
  };

  return (
    <div className={cn("overflow-hidden rounded-lg border", className)}>
      <Table>
        <TableHeader>
          <TableRow>
            {selectable && (
              <TableHead className="w-12">
                <Checkbox
                  checked={isSomeSelected ? "indeterminate" : isAllSelected}
                  onCheckedChange={handleSelectAll}
                  aria-label="Select all"
                />
              </TableHead>
            )}
            {columns.map((column) => {
              const isSorted = sortState?.column === column.id;
              const sortDirection = isSorted ? sortState?.direction : null;

              return (
                <TableHead key={column.id} className={column.className}>
                  {column.sortable ? (
                    <button
                      className={cn(
                        "-m-1 flex items-center space-x-2 rounded p-1 text-left text-foreground-muted hover:text-primary-600",
                        isSorted && "font-semibold"
                      )}
                      onClick={() => onSort?.(column.id)}
                      type="button"
                    >
                      <span>{column.header}</span>
                      <div className="flex flex-col">
                        {sortDirection === "asc" && <ArrowUp className="size-4" />}
                        {sortDirection === "desc" && (
                          <ArrowDown className="size-4" />
                        )}
                      </div>
                    </button>
                  ) : (
                    column.header
                  )}
                </TableHead>
              );
            })}
            {showActions && actions.length > 0 && (
              <TableHead className="w-24 text-center">
                {actionsHeaderLabel}
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell
                colSpan={
                  columns.length +
                  (selectable ? 1 : 0) +
                  (showActions && actions.length > 0 ? 1 : 0)
                }
                className="py-8 text-center"
              >
                <div className="flex items-center justify-center">
                  {loadingComponent || (
                    <LoadingIcon className="size-8 animate-spin-ccw" />
                  )}
                </div>
              </TableCell>
            </TableRow>
          ) : data.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={
                  columns.length +
                  (selectable ? 1 : 0) +
                  (showActions && actions.length > 0 ? 1 : 0)
                }
                className="py-8 text-center text-gray-500"
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            data.map((item) => {
              const itemId = getItemId(item);
              const isSelected = selectedItems.includes(itemId);

              return (
                <TableRow
                  key={itemId}
                  className={cn(
                    // Cyan wash on hover (transient), blue wash when selected
                    // (persistent) — same secondary-50 the active Filter chip
                    // uses, so "selected" reads as the app's established
                    // active-state color rather than blending into the hover
                    // cyan at barely-different opacity.
                    "cursor-pointer hover:bg-[#E8FDFF]/50",
                    isSelected && "bg-secondary-50"
                  )}
                  onClick={() => onRowClick?.(item)}
                >
                  {selectable && (
                    <TableCell className={cellClassName}>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) =>
                          handleSelectItem(itemId, checked as boolean)
                        }
                        aria-label={`Select item ${itemId}`}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TableCell>
                  )}
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      className={cn(cellClassName, column.className)}
                    >
                      {renderCell(getCellValue(item, column))}
                    </TableCell>
                  ))}
                  {showActions && actions.length > 0 && (
                    <TableCell className={cellClassName}>
                      <div className="flex items-center justify-center gap-2">
                        {actions.map((action, index) => (
                          <div key={index} onClick={(e) => e.stopPropagation()}>
                            {action.component(item)}
                          </div>
                        ))}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
};
